import { Stack } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';

import CheckPermissions from '@/components/CheckPermissions';
import ContentNotification from '@/components/ContentNotfication';
import PERMISSIONS from '@/constants/auth.constants';
import { PRODUCTS } from '@/constants/products.constants';
import AccountManager from '@/features/AdminAccountManager';
import { AdminApiDocsLink } from '@/features/AdminApiDocsLink';
import AutoCloseConfiguration from '@/features/AdminAutoCloseConfiguration';
import ManagedEDR from '@/features/AdminManagedEDR';
import { MultifactorAuthentication } from '@/features/AdminMultifactorAuthentication';
import { OrganizationInformation } from '@/features/AdminOrganizationInformation';
import { PreferencesConfiguration } from '@/features/AdminPreferencesConfiguration';
import { TagManagement } from '@/features/AdminTagManagement';
import { PonduranceContactInfo } from '@/features/PonduranceContactInfo';
import { useProductsCheck } from '@/hooks/useProductsCheck';
import { selectedOrganizationIdAtom } from '@/pages/Organizations';
import { userIsInternalAtom } from '@/store/user.store';

import { useFetchSettings, useMutationSettings } from '../Administration.store';
import type { SettingsTypes } from '../Administration.types';

const AdministrationGeneral = () => {
  const [selectedOrganizationId] = useAtom(selectedOrganizationIdAtom);
  const { refetch, data: orgInfo } = useFetchSettings();
  const mutation = useMutationSettings(refetch);
  const userIsInternal = useAtomValue(userIsInternalAtom);

  const handleSave = (updatedOrgInfo: SettingsTypes) => {
    mutation.mutate(updatedOrgInfo);
  };

  const hasEdr = useProductsCheck([PRODUCTS.EDR]);

  return (
    <>
      {selectedOrganizationId === 0 && (
        <ContentNotification
          title="Select an organization"
          text="Please select an organization to view and edit its settings."
        />
      )}
      {selectedOrganizationId !== 0 && (
        <Stack spacing={2}>
          <Stack sx={{ gap: 2 }} direction={{ sm: 'column', md: 'row' }}>
            <PonduranceContactInfo />
            <CheckPermissions permissions={PERMISSIONS.INTERNAL}>
              <OrganizationInformation />
            </CheckPermissions>
          </Stack>
          <AdminApiDocsLink />
          <MultifactorAuthentication
            orgInfo={orgInfo}
            handleSave={handleSave}
          />
          <AccountManager />
          <CheckPermissions permissions={PERMISSIONS.INTERNAL}>
            <TagManagement orgInfo={orgInfo} handleSave={handleSave} />
          </CheckPermissions>
          <PreferencesConfiguration handleSave={handleSave} orgInfo={orgInfo} />
          <CheckPermissions permissions={PERMISSIONS.TICKETS_UPDATE}>
            <AutoCloseConfiguration handleSave={handleSave} orgInfo={orgInfo} />
          </CheckPermissions>
          {(hasEdr || userIsInternal || orgInfo?.display_host_isolation) && (
            <ManagedEDR handleSave={handleSave} orgInfo={orgInfo} />
          )}
        </Stack>
      )}
    </>
  );
};

export default AdministrationGeneral;
